import { TimelineSlot } from './TimelineSlot';
import { Card, Select, Tag, Badge, Icon, IconButton } from '../design-system/components';

/**
 * Timeline panel component
 * Displays available field slots grouped by venue and field
 */
export const TimelinePanel = ({
  groupedSlots,
  selectedRequest,
  selectedDate,
  setSelectedDate,
  uniqueDates,
  collapsedFields,
  toggleFieldCollapse,
  onAssign,
  onChangeField,
  onCancelBooking,
  onDragOver,
  onDragLeave,
  onDrop,
  findStackingOpportunities,
  getAssignedRequest,
  isPreferredTime,
  isAdjacentTime,
  venues,
  selectedVenues,
  setSelectedVenues
}) => {
  const requestedVenue = selectedRequest?.['Requested Venue'] || '';

  const toggleVenue = (venue) => {
    setSelectedVenues((prev) => {
      const next = new Set(prev);
      if (next.has(venue)) {
        next.delete(venue);
      } else {
        next.add(venue);
      }
      return next;
    });
  };
  return (
    <Card padding="var(--space-5)" className="flex flex-col h-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--text-xl)', color: 'var(--text-strong)' }}>
          Available Fields Timeline
        </h2>
        {uniqueDates && uniqueDates.length > 0 && (
          <Select
            size="sm"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            containerStyle={{ minWidth: 160 }}
          >
            {uniqueDates.map((date) => (
              <option key={date} value={date}>
                {date}
              </option>
            ))}
          </Select>
        )}
      </div>

      {/* Venue Toggles - only when a request is selected */}
      {selectedRequest && venues && venues.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {venues.map((venue) => {
            const isActive = selectedVenues.has(venue);
            const isRequested = venue === requestedVenue;
            return (
              <Tag key={venue} selected={isActive} onClick={() => toggleVenue(venue)}>
                {venue}{isRequested ? ' *' : ''}
              </Tag>
            );
          })}
        </div>
      )}

      {/* Field Groups */}
      <div className="flex-1 overflow-y-auto space-y-4">
        {!selectedRequest ? (
          <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
            <Icon name="arrow-left" size={32} color="var(--text-muted)" style={{ margin: '0 auto var(--space-2)' }} />
            <div>Select a request to view available fields</div>
          </div>
        ) : Object.keys(groupedSlots).length === 0 ? (
          <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
            <Icon name="calendar" size={32} color="var(--text-muted)" style={{ margin: '0 auto var(--space-2)' }} />
            <div>No available fields for this date and venue</div>
          </div>
        ) : (
          Object.entries(groupedSlots).map(([key, fieldGroup]) => {
            const targetDuration = selectedRequest
              ? selectedRequest['Field Size'] === '11v11'
                ? 2
                : 1.5
              : 1.5;
            const opportunities = findStackingOpportunities(
              fieldGroup,
              targetDuration
            );
            const isAltVenue = fieldGroup.venue !== requestedVenue;

            return (
              <div
                key={key}
                style={{
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  border: `2px solid ${isAltVenue ? 'var(--secondary)' : 'var(--border)'}`,
                  background: isAltVenue ? 'var(--secondary-soft)' : 'transparent',
                }}
              >
                <h3 className="flex items-center justify-between mb-3" style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)' }}>
                  <div className="flex items-center gap-2">
                    <span>
                      {fieldGroup.venue} - Field {fieldGroup.field}
                    </span>
                    {isAltVenue && <Badge tone="info" variant="solid">Alt venue</Badge>}
                    <IconButton
                      icon={collapsedFields.has(key) ? 'chevron-right' : 'chevron-down'}
                      size="sm"
                      variant="ghost"
                      label={collapsedFields.has(key) ? 'Expand slots' : 'Collapse slots'}
                      onClick={() => toggleFieldCollapse(key)}
                    />
                  </div>
                  {opportunities.length > 0 && (
                    <Badge tone="success" variant="solid" icon="zap">
                      {opportunities.length} Stacking{' '}
                      {opportunities.length === 1
                        ? 'Opportunity'
                        : 'Opportunities'}
                    </Badge>
                  )}
                </h3>

                <div
                  className="transition-all duration-200 overflow-hidden"
                  style={{ maxHeight: collapsedFields.has(key) ? 0 : 2000 }}
                >
                  <div className="space-y-2">
                    {fieldGroup.slots.map((slot, idx) => {
                      const assignedReq = getAssignedRequest(slot);
                      const isStacking = opportunities.some((opp) =>
                        opp.slots.includes(slot)
                      );
                      const isBooked = slot.IsBooked === 'TRUE';
                      const isPrefTime = isPreferredTime(slot, selectedRequest);
                      const isAdjTime = isAdjacentTime(
                        slot,
                        selectedRequest?.['Preferred Time'],
                        targetDuration
                      );

                      return (
                        <TimelineSlot
                          key={`${key}-${idx}`}
                          slot={slot}
                          isPreferredTime={isPrefTime}
                          isAdjacentTime={isAdjTime}
                          isStacking={isStacking}
                          isBooked={isBooked}
                          assignedRequest={assignedReq}
                          selectedRequest={selectedRequest}
                          onAssign={onAssign}
                          onChangeField={onChangeField}
                          onCancelBooking={onCancelBooking}
                          onDragOver={onDragOver}
                          onDragLeave={onDragLeave}
                          onDrop={onDrop}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
};
